// Personnel detail page. Reached via /personnel/:id. Reads the row from
// Supabase + assigned tags, then renders the read-only field grid.
// Edit button → /personnel/:id/edit.

import React, {useEffect, useState} from 'react'
import clsx from 'clsx'
import {useNavigate, useParams} from 'react-router-dom'
import {Avatar} from '../../components/common/Avatar'
import {Header} from '../../components/Header'
import {PageShell} from '../../components/PageShell'
import {TagBadge} from '../../components/common/TagBadge'
import {showToast} from '../../components/common/Toast'
import {supabase} from '../../app/supabaseClient'
import type {PersonnelDetail, PersonnelStatus, PersonnelTag} from './types'

const STATUS_LABEL: Record<PersonnelStatus, string> = {
  tagged_in: 'Tagged in',
  tagged_out: 'Tagged out',
  missing: 'Missing',
}

const STATUS_PILL_CLASS: Record<PersonnelStatus, string> = {
  tagged_in: 'bg-badgeGreenBg text-statusGreen',
  tagged_out: 'bg-surfaceLight text-textMuted',
  missing: 'bg-badgeRedBg text-statusRed',
}

export const PersonnelDetailScreen: React.FC = () => {
  const {id} = useParams<{id: string}>()
  const navigate = useNavigate()
  const [person, setPerson] = useState<PersonnelDetail | null>(null)
  const [tags, setTags] = useState<PersonnelTag[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!id) return
    let cancelled = false
    ;(async () => {
      setLoading(true)
      const [{data: p, error: pe}, {data: t, error: te}] = await Promise.all([
        supabase
          .from('personnel')
          .select('id, name, employee_id, designation, crew, status, shift_id, location, photo')
          .eq('id', id)
          .single(),
        supabase
          .from('tags')
          .select('id, tag_id, battery_level, signal_strength, is_connected')
          .eq('assigned_to_id', id)
          .eq('assigned_to_type', 'personnel'),
      ])
      if (cancelled) return
      if (pe) setError(pe.message)
      else setPerson(p as PersonnelDetail)
      if (te) setError(te.message)
      else setTags((t as PersonnelTag[]) ?? [])
      setLoading(false)
    })()
    return () => {
      cancelled = true
    }
  }, [id])

  const handleUnassignTag = async (tag: PersonnelTag) => {
    if (!window.confirm(`Unassign tag #${tag.tag_id}?`)) return
    const {error: e} = await supabase
      .from('tags')
      .update({assigned_to_id: null, assigned_to_type: null})
      .eq('id', tag.id)
    if (e) {
      showToast(`Unassign failed: ${e.message}`, 'error')
      return
    }
    setTags(prev => prev.filter(t => t.id !== tag.id))
    showToast('Tag unassigned.', 'success')
  }

  return (
    <main className="flex flex-1 flex-col overflow-hidden">
      <Header
        title={person?.name ?? 'Personnel'}
        backTo="/personnel"
      />
      <PageShell
        footer={
          person && (
            <button
              type="button"
              onClick={() => navigate(`/personnel/${person.id}/edit`)}
              className="rounded-md border border-primary px-lg py-sm text-md font-semibold text-primary hover:bg-primaryBg"
            >
              Edit
            </button>
          )
        }
      >
        {loading ? (
          <div className="flex h-full items-center justify-center text-textMuted">Loading…</div>
        ) : error || !person ? (
          <div className="rounded-lg border border-statusRed bg-surfaceCard p-xxl">
            <h3 className="mb-md text-lg font-semibold text-statusRed">Fetch failed</h3>
            <pre className="whitespace-pre-wrap font-mono text-sm text-textPrimary">{error ?? 'Not found'}</pre>
          </div>
        ) : (
          <div className="flex flex-col gap-xl">
            {/* Header — avatar + summary */}
            <div className="flex items-center gap-lg">
              <Avatar uri={person.photo} name={person.name} size={64} />
              <div>
                <div className="text-xxl font-bold text-textPrimary">{person.name}</div>
                <div className="text-md text-textMuted">{person.employee_id}</div>
                <div className="mt-xs text-sm text-textSecondary">
                  {person.designation} · {person.crew}
                  {person.shift_id && <> · {person.shift_id} shift</>}
                </div>
              </div>
              <div className="ml-auto">
                <span
                  className={clsx(
                    'rounded-round px-md py-xs text-xs font-semibold uppercase',
                    STATUS_PILL_CLASS[person.status],
                  )}
                >
                  {STATUS_LABEL[person.status]}
                </span>
              </div>
            </div>

            {/* Field grid */}
            <dl className="grid grid-cols-2 gap-x-xl gap-y-md rounded-md border border-surfaceBorder bg-surfaceCard p-lg">
              <ViewField label="Designation" value={person.designation ?? '—'} />
              <ViewField label="Crew" value={person.crew ?? '—'} />
              <ViewField label="Status" value={STATUS_LABEL[person.status]} />
              <ViewField label="Shift" value={person.shift_id ?? '—'} />
              <ViewField label="Location" value={person.location ?? '—'} />
            </dl>

            {/* Tags */}
            <div className="rounded-md border border-surfaceBorder bg-surfaceCard p-lg">
              <div className="mb-md text-sm font-semibold uppercase tracking-wider text-textMuted">
                Assigned Tags ({tags.length})
              </div>
              {tags.length === 0 ? (
                <div className="text-sm text-textMuted">
                  No tags assigned. Pair via BLE in the mobile app.
                </div>
              ) : (
                <ul className="flex flex-col gap-sm">
                  {tags.map(t => (
                    <li
                      key={t.id}
                      className="flex items-center gap-md rounded-md bg-surface px-md py-sm"
                    >
                      <TagBadge tagId={t.tag_id} isActive={t.is_connected} size="medium" />
                      <span className="text-sm text-textSecondary">
                        Battery {t.battery_level}% · {t.signal_strength}
                      </span>
                      <span className="ml-auto text-xs text-textMuted">
                        {t.is_connected ? 'Connected' : 'Disconnected'}
                      </span>
                      <button
                        type="button"
                        onClick={() => handleUnassignTag(t)}
                        className="rounded-md border border-statusRed px-md py-xs text-xs text-statusRed hover:bg-statusRed hover:text-white"
                      >
                        Unassign
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}
      </PageShell>
    </main>
  )
}

const ViewField: React.FC<{label: string; value: string}> = ({label, value}) => (
  <div>
    <dt className="text-xs uppercase tracking-wider text-textMuted">{label}</dt>
    <dd className="mt-1 text-md text-textPrimary">{value}</dd>
  </div>
)
